import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import TablePagination from '@material-ui/core/TablePagination';
import { useParams } from 'react-router';

import { addPharmacyNote, getPharmacyNotes } from '../../redux/notes';
import { getPharmacy } from '../../redux/pharmacy';
import * as orderActions from '../../redux/orders';

import HealtheraTable from '../Table/Table';
import { colors, ErrorBox } from '../../healtheraComponents';
import { formatDateTimeHumanFriendly } from '../../utils/formatters';
import { getOrderFulfillmentInfo, getOrderType } from '../../helpers';
import PharmacyBriefDetails from './PharmacyBriefDetails';
import NotesModal from '../Notes/NotesModal';

import OrderFlag from './OrderFlag';
import { DEFAULT_ORDER_LIST_LIMIT } from './constants';
import useLocationQuery from '../../hooks/useQuery';
import { setDisplayMenu } from '../../redux/options/actions';
import TableContainer from '../Shared/TableContainer';
import SlaTimeslot from '../DashboardPage/components/SlaTimeslot';

const PharamcyOrderList = () => {
  const dispatch = useDispatch();
  const { changeLocationQuery, query } = useLocationQuery();

  const { pharmacyId } = useParams();

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);

  const { orders, error, totalOrders } = useSelector(state => state.orders);
  const { user } = useSelector(state => state.authentication);
  const { notes } = useSelector(state => state.notes);
  const { pharmacy, isLoading } = useSelector(state => state.pharmacy);

  const handleRowsPerPageChange = useCallback(
    ({ target: { value } }) => {
      changeLocationQuery({
        ...query,
        limit: value,
        page: 0
      });
    },
    [changeLocationQuery, query]
  );

  const handleChangePage = useCallback(
    (value, pageNumber) => {
      changeLocationQuery({
        ...query,
        page: pageNumber
      });
    },
    [changeLocationQuery, query]
  );

  const toggleNoteModalOpen = () => {
    setIsNoteModalOpen(prevIsNoteModalOpen => !prevIsNoteModalOpen);
  };

  const handleAddPharmacyNote = text => {
    dispatch(addPharmacyNote(pharmacyId, text));
  };

  useEffect(() => {
    dispatch(setDisplayMenu(false));
    dispatch(getPharmacyNotes(pharmacyId));
    dispatch(getPharmacy(pharmacyId));
  }, [dispatch, pharmacyId]);

  useEffect(() => {
    dispatch(
      orderActions.getOrders({
        page: +query.page || 0,
        pharmacyID: pharmacyId || null,
        limit: +query.limit || DEFAULT_ORDER_LIST_LIMIT,
      })
    );
  }, [dispatch, query, pharmacyId]);

  const preparedData = useMemo(() => prepareData(orders), [orders]);

  return (
    <>
      <TableContainer>
        <TableContainerHeader>{pharmacy?.pharmacy_name || 'Loading..'}</TableContainerHeader>
        {error && <ErrorBox>{error}</ErrorBox>}
        <TipContainer>
          <Tip>* Only last month orders are displayed</Tip>
        </TipContainer>
        <TableContainerContent>
          <TableWrapper>
            <HealtheraTable
              muted={true}
              data={preparedData}
              onChangePage={handleChangePage}
              onRowsPerPageChange={handleRowsPerPageChange}
              displayPagination
              totalCount={totalOrders}
              page={+query.page || 0}
              rowsPerPage={+query.limit || DEFAULT_ORDER_LIST_LIMIT}
              rowsPerPageOptions={['5', '10', '15', '20']}
            />
          </TableWrapper>
          <PharmacyDetailsWrapper>
            <PharmacyBriefDetails
              isLoading={isLoading}
              pharmacy={pharmacy}
              onNotesClick={toggleNoteModalOpen}
              lastNote={notes && notes[0]}
            />
          </PharmacyDetailsWrapper>
        </TableContainerContent>
        <NotesModal
          notes={notes}
          open={isNoteModalOpen}
          onRequestClose={toggleNoteModalOpen}
          onAddNote={handleAddPharmacyNote}
          user={user}
        />
      </TableContainer>
    </>
  );
};

const prepareData = (orders = []) => {
  return orders.reduce(
    (data, order = {}) => {
      const {
        date_created,
        timestamp,
        patient,
        repeat,
        _id,
        pharmacy_id,
        rag_status,
        sla,
        status,
      } = order;
      const { forename, surname } = patient || {};
      const { period: { start: slaStart, end: slaEnd } = {}, type: sla_type } =
        sla || {};

      const { flagged, commissionable } = repeat || {};

      const { timeslot, type } = getOrderFulfillmentInfo(order);

      data.push({
        view: {
          name: (
            <PatientNameContainer>
              {commissionable && <ReferredFlag>Referred</ReferredFlag>}
              {[forename, surname].join(' ')}
            </PatientNameContainer>
          ),
          priorityStatus: (
            <SlaTimeslot
              rag={rag_status}
              slaType={sla_type}
              slaTimeslot={{ slaStart, slaEnd }}
            />
          ),
          orderType: getOrderType(order),
          orderedDate: formatDateTimeHumanFriendly(date_created * 1000),
          lastUpdated: formatDateTimeHumanFriendly(timestamp * 1000),
          fulfillmentType: (
            <Pillar>
              <SpanBlock>{type}</SpanBlock>
              <SpanBlock>{timeslot}</SpanBlock>
              {flagged && (
                <FlagContainer>
                  <OrderFlag hint={flagged?.reason} />
                </FlagContainer>
              )}
            </Pillar>
          ),
          status,
        },
        ID: _id,
        route: `problematic-pharmacies/${pharmacy_id}`,
      });
      return data;
    },
    [
      {
        view: {
          name: 'Patient',
          priorityStatus: { title: 'Priority Status', width: '20%' },
          orderType: { title: 'Order Type', width: '10%' },
          orderedDate: 'Ordered Date',
          lastUpdated: 'Last Updated',
          fulfillmentType: 'Fulfillment Type',
          status: 'Status',
        },
        headers: true,
      },
    ],
  );
};

export default PharamcyOrderList;

export const TableWrapper = styled.div`
  width: 70%;
  background-color: white;
  flex-shrink: 0;
`;

export const TableContainerHeader = styled.div`
  padding: 8px 24px;
  background-color: ${colors.HEALTHERA_GREEN};
  color: white;
  font-size: 17px;
  line-height: 21px;
`;

export const TableContainerContent = styled.div`
  display: flex;
  padding: 16px;
`;

export const TablePaginationStyled = styled(TablePagination)`
  align-items: center;
  color: ${colors.GRAY_DARKER};
  display: flex;
  flex: 0 0 100%;
  font-size: 13px;
  justify-content: flex-end;
  padding-top: 20px;
  text-align: right;
`;

const PharmacyDetailsWrapper = styled.div`
  flex-grow: 1;
  box-sizing: border-box;
  margin-left: 8px;
`;

const SpanBlock = styled.span`
  display: block;
`;

const Pillar = styled.p`
  position: relative;
  margin: 0;
  min-height: 53px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FlagContainer = styled.span`
  margin: 0;
  position: absolute;
  right: 0;
  top: calc(50% - 15px);
`;

const PatientNameContainer = styled.p`
  padding-top: 25px;
  padding-bottom: 15px;
  position: relative;
  margin: 0;
`;

const ReferredFlag = styled.span`
  position: absolute;
  top: 5px;
  left: 0px;
  font-size: 10px;
  background-color: ${colors.BLUE};
  color: white;
  font-weight: 600;
  padding: 3px 5px;
  border-radius: 10px;
`;

const TipContainer = styled.div`
  margin-top: 20px;
  margin-left: 20px;
`

const Tip = styled.span`
  font-size: 12px;
  opacity: 0.6;
`;
